import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Document {
  _id: string;
  title: string;
  content: string;
  required: boolean;
  order: number;
  category: 'onboarding' | 'pre-application';
  createdAt: string;
  updatedAt: string;
}

interface DocumentConfirmation {
  documentId: string;
  confirmed: boolean;
  confirmedAt?: string;
}

interface CounsellorDocuments {
  _id: string;
  counsellorId: string;
  confirmations: DocumentConfirmation[];
}

interface DocumentConfirmationProps {
  counsellorId: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  readOnly?: boolean;
}

const DocumentConfirmationComponent: React.FC<DocumentConfirmationProps> = ({
  counsellorId,
  onSuccess,
  onError,
  readOnly = false
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [counsellorDocuments, setCounsellorDocuments] = useState<CounsellorDocuments | null>(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch only onboarding documents from the backend
      const documentsResponse = await api.get('/api/documents?category=onboarding');
      setDocuments(documentsResponse.data);
      
      // Fetch counsellor's document confirmations
      const counsellorDocumentsResponse = await api.get(`/api/counsellors/${counsellorId}/documents`);
      setCounsellorDocuments(counsellorDocumentsResponse.data);
      
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, [counsellorId, onError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDocument = (document: Document) => {
    setCurrentDocument(document);
    setConfirmChecked(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentDocument(null);
  };

  const handleConfirmDocument = async () => {
    if (!currentDocument || !confirmChecked) return;
    
    try {
      setLoading(true);
      
      await api.post(`/api/counsellor-documents/${counsellorId}/documents/${currentDocument._id}/confirm`);
      
      onSuccess(`Document "${currentDocument.title}" confirmed successfully`);
      await fetchData(); // Refresh data
      handleCloseDialog();
      
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to confirm document');
    } finally {
      setLoading(false);
    }
  };

  const isDocumentConfirmed = (documentId: string): boolean => {
    if (!counsellorDocuments) return false;
    
    const confirmation = counsellorDocuments.confirmations.find(
      c => c.documentId === documentId
    );
    
    return confirmation ? confirmation.confirmed : false;
  };

  const getConfirmationDate = (documentId: string): string | undefined => {
    if (!counsellorDocuments) return undefined;
    
    const confirmation = counsellorDocuments.confirmations.find(
      c => c.documentId === documentId
    );
    
    return confirmation?.confirmedAt;
  };


  const getRequiredDocumentsCount = (): number => {
    return documents.filter(doc => doc.required).length;
  };

  const getConfirmedRequiredDocumentsCount = (): number => {
    if (!counsellorDocuments) return 0;
    
    return documents
      .filter(doc => doc.required)
      .filter(doc => isDocumentConfirmed(doc._id))
      .length;
  };

  const allRequiredDocumentsConfirmed = (): boolean => {
    return getConfirmedRequiredDocumentsCount() === getRequiredDocumentsCount();
  };

  return (
    <Box>
      {loading && !dialogOpen && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {!loading && documents.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No documents have been added yet.
        </Alert>
      )}
      
      {!loading && documents.length > 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Document Confirmations
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {readOnly 
                ? 'Review the counsellor\'s document confirmations below.'
                : 'Please read and confirm the following documents to complete your onboarding.'}
            </Typography>
            
            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body2">
                Progress: {getConfirmedRequiredDocumentsCount()} of {getRequiredDocumentsCount()} required documents confirmed
                {allRequiredDocumentsConfirmed() && (
                  <CheckCircleIcon color="success" sx={{ ml: 1, verticalAlign: 'middle' }} />
                )}
              </Typography>
            </Box>
          </Box>
          
          <List>
            {documents.map((document) => {
              const isConfirmed = isDocumentConfirmed(document._id);
              const confirmationDate = getConfirmationDate(document._id);
              
              return (
                <Card key={document._id} sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">
                          {document.title}
                          {document.required && (
                            <Typography component="span" color="error" sx={{ ml: 1 }}>
                              *
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      
                      {isConfirmed && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="success.main">
                            Confirmed
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    {isConfirmed && confirmationDate && (
                      <Typography variant="caption" color="text.secondary">
                        Confirmed on {new Date(confirmationDate).toLocaleDateString()}
                      </Typography>
                    )}
                    
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions>
                    {!readOnly && (
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDocument(document)}
                        disabled={isConfirmed}
                      >
                        {isConfirmed ? 'Already Confirmed' : 'Read & Confirm'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              );
            })}
          </List>
        </>
      )}
      
      {/* Document Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {currentDocument && (
          <>
            <DialogTitle>
              {currentDocument.title}
              {currentDocument.required && (
                <Typography component="span" color="error" sx={{ ml: 1 }}>
                  *
                </Typography>
              )}
            </DialogTitle>
            
            <DialogContent dividers>
              <Box sx={{ 
                mb: 3,
                color: 'text.primary',
                lineHeight: 1.7,
                '& p': { mb: 2 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 1 },
                '& h1': { fontSize: '2rem', fontWeight: 700, mb: 2, mt: 2 },
                '& h2': { fontSize: '1.5rem', fontWeight: 600, mb: 2, mt: 2 },
                '& h3': { fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 1 },
                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                '& th': { border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', fontWeight: 600, textAlign: 'left' },
                '& td': { border: '1px solid #ddd', padding: '8px' },
                '& strong': { fontWeight: 600 }
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentDocument.content}</ReactMarkdown>
              </Box>
              
              {!readOnly && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmChecked}
                      onChange={(e) => setConfirmChecked(e.target.checked)}
                    />
                  }
                  label="I confirm that I have read and understood this document"
                />
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                Close
              </Button>
              
              {!readOnly && (
                <Button
                  onClick={handleConfirmDocument}
                  color="primary"
                  variant="contained"
                  disabled={!confirmChecked || loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Confirm'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DocumentConfirmationComponent;
